import {
  getAllMovies,
  getAllShowsComplete,
  getAllShows,
  syncActivities,
} from '../utils/api';
import db, {
  dbReady,
  DETAIL_MOVIES_TABLE,
  DETAIL_SHOWS_TABLE,
  USER_MOVIES_TABLE,
  USER_SHOWS_TABLE,
} from '../utils/db';
import { store } from './store';
import { fillDetail } from './slices/movies/thunks';
import { fillDetail as fillDetailShow } from './slices/shows/thunks';
import { Activities } from '../models/Api';

const DETAIL_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_STALE_DETAIL_REFRESH = 20;

const syncRemoteMovies = async (
  oldActivities: Activities | null,
  newActivities: Activities | null
) => {
  if (oldActivities?.movies?.removed !== newActivities?.movies.removed) {
    const allMoviesIds = await getAllMovies().then(({ data }) =>
      data?.map((m) => m.movie_tmdb)
    );
    const userMovies = await db.table(USER_MOVIES_TABLE).toArray();
    const moviesToDelete = userMovies.filter(
      (um) => !allMoviesIds?.includes(um.movie_tmdb)
    );
    await db
      .table(USER_MOVIES_TABLE)
      .bulkDelete(moviesToDelete.map((m) => m.movie_tmdb));
  }

  if (
    !oldActivities?.movies?.rest ||
    oldActivities.movies.rest !== newActivities?.movies.rest
  ) {
    const { data: allMovies } = await getAllMovies(oldActivities?.movies?.rest);
    if (allMovies) {
      await db
        .table(USER_MOVIES_TABLE)
        .bulkPut(allMovies.map((m) => ({ ...m, movie_tmdb: Number(m.movie_tmdb) })));
    }
  }
};

const syncRemoteShows = async (
  oldActivities: Activities | null,
  newActivities: Activities | null
) => {
  if (oldActivities?.shows?.removed !== newActivities?.shows.removed) {
    const allShowsIds = await getAllShows().then(({ data }) =>
      data?.map((s) => s.show_tmdb)
    );
    const userShows = await db.table(USER_SHOWS_TABLE).toArray();

    const showsToDelete = userShows.filter(
      (us) => !allShowsIds?.includes(us.show_tmdb)
    );
    await db
      .table(USER_SHOWS_TABLE)
      .bulkDelete(showsToDelete.map((s) => s.show_tmdb));
  }

  if (oldActivities?.shows?.rest !== newActivities?.shows.rest) {
    const { data: allShows } = await getAllShowsComplete(
      oldActivities?.shows?.rest
    );
    if (allShows) {
      await db
        .table(USER_SHOWS_TABLE)
        .bulkPut(
          allShows.map((s) => ({
            ...s,
            show_tmdb: Number(s.show_tmdb),
            episodes: (s.episodes ?? []).map((e) => ({
              ...e,
              episode_tmdb: Number(e.episode_tmdb),
              show_tmdb: Number(e.show_tmdb),
            })),
          }))
        );
    }
  }
};

let firstLoadPromise: Promise<boolean> | null = null;

export const firstLoad = (): Promise<boolean> => {
  if (firstLoadPromise) {
    return firstLoadPromise;
  }
  firstLoadPromise = firstLoadInternal();
  firstLoadPromise.finally(() => {
    firstLoadPromise = null;
  });
  return firstLoadPromise;
};

const firstLoadInternal = async (): Promise<boolean> => {
  try {
    await dbReady;
    const oldActivities: Activities | null = JSON.parse(
      localStorage.getItem('activities') ?? '{}'
    );
    const { data: newActivities } = await syncActivities();

    await Promise.all([
      syncRemoteMovies(oldActivities, newActivities),
      syncRemoteShows(oldActivities, newActivities),
    ]);

    const localUserMovieWatchlistIds = await db
      .table<any, number>(USER_MOVIES_TABLE)
      .where({ status: 'watchlist' })
      .primaryKeys();
    const localUserMovieWatchedIds = await db
      .table<any, number>(USER_MOVIES_TABLE)
      .where({ status: 'watched' })
      .primaryKeys();
    const localDetailMovieIds = await db
      .table<any, number>(DETAIL_MOVIES_TABLE)
      .toCollection()
      .primaryKeys();

    const movieIdsToFill = [
      ...localUserMovieWatchlistIds,
      ...localUserMovieWatchedIds,
    ].filter((id) => !localDetailMovieIds.includes(id));

    movieIdsToFill.forEach((id) => {
      store.dispatch(fillDetail({ id }));
    });

    const localUserShowIds = await db
      .table<any, number>(USER_SHOWS_TABLE)
      .toCollection()
      .primaryKeys();
    const localDetailShowIds = await db
      .table<any, number>(DETAIL_SHOWS_TABLE)
      .toCollection()
      .primaryKeys();

    const showIdsToFill = localUserShowIds.filter(
      (id) => !localDetailShowIds.includes(id)
    );

    showIdsToFill.forEach((id) => {
      store.dispatch(fillDetailShow({ id }));
    });

    const now = Date.now();
    const staleDetailShowIds = (
      await db[DETAIL_SHOWS_TABLE]
        .where('ids.tmdb')
        .anyOf(localUserShowIds)
        .toArray()
    )
      .filter(
        (show) =>
          show.status !== 'ended' &&
          show.status !== 'canceled' &&
          (!show.updatedAt ||
            now - Date.parse(show.updatedAt) > DETAIL_TTL_MS)
      )
      .map((show) => show.ids.tmdb)
      .filter((id) => !showIdsToFill.includes(id))
      .slice(0, MAX_STALE_DETAIL_REFRESH);

    staleDetailShowIds.forEach((id) => {
      store.dispatch(fillDetailShow({ id }));
    });

    localStorage.setItem('activities', JSON.stringify(newActivities));
    return true;
  } catch (e) {
    console.error('Error on firstLoad');
    console.error(e);
    return false;
  }
};
